// Вкладки SCR-STORE кроме «Талонов» (E12-T11, ux/08 §1.3): «Наборы», «Лавка», «Ящики», «Энергия», «Ускорения»,
// «Вид», «Всякое» — и тело MOD-PURCHASE для товара за талоны (§2.1, §2.2). Цены, пределы и что продаётся —
// с сервера (store-model.ts); клиент их не хранит и цену из кэша не показывает (§1.5).
import { signal } from '@preact/signals';
import { useRef } from 'preact/hooks';
import type { Api, Grant, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { amount, num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { itemName, itemDesc } from '../content/catalog.js';
import { ApiCallError } from '../net/api-client.js';
import { features, online, setWallet, shop, wallet } from '../state/game.js';
import { showToast } from '../ui/toast.js';
import { I, type IconName } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { assetEntry } from '../assets/manifest.js';
import { boxArt } from './boxes.js';
import { baseRate, PACK_NAMES } from '../pay/flow.js';
import {
  buyState, coverPack, tn, inDebt, storeExtras, talonsHint, type BuyState, type StoreExtras, type TalonGood, type ThermosState,
} from './store-model.js';
import { HintLine, HoldButton, Stars, StatsCompare, TalonPrice, StarsIcon } from './store-ui.js';
import { slotOf } from '../content/sets.js';

export type ShopData = ResponseOf<Api['getShop']>;
type StarProduct = ShopData['sections'][number]['products'][number];

/** Перечитать витрину после покупки за талоны (пределы и «Куплено ✓» считает сервер). */
export const shopVersion = signal(0);
export const bumpShop = () => { shopVersion.value++; };

/** Последний ответ POST /energy/thermos — свежее данных витрины, пока она не перечитана. */
export const thermosLocal = signal<(ThermosState & { at: number }) | null>(null);

export function useThermos(): ThermosState | null {
  const ex = useExtras(shop.value?.data ?? null);
  const loc = thermosLocal.value;
  return loc && loc.at >= (shop.value?.at ?? 0) ? loc : ex.thermos;
}

export function useExtras(shop: ShopData | null): StoreExtras {
  return storeExtras(shop, features.value);
}

export function starPacks(shop: ShopData | null): StarProduct[] {
  return shop?.sections.find((s) => s.id === 'talons')?.products ?? [];
}

/** Подписи товаров за талоны по ID (ID — Р-49); неизвестный товар — общим словом, ID не показываем. */
const GOOD_KEYS: Record<string, string> = {
  'GOOD-THERMOS': 'CL-STORE-G-THERMOS', 'GOOD-BOOST-W01': 'CL-STORE-G-BOOST', 'GOOD-NUTS-BUCKET': 'CL-STORE-G-NUTS',
  'GOOD-BOX-MASTER': 'CL-STORE-G-BOX', 'GOOD-BOX-MASTER-10': 'CL-STORE-G-BOX10', 'GOOD-SHOWCASE': 'SHOP-SHOWCASE-TITLE',
  'GOOD-SEMYONYCH-GOOD': 'CL-STORE-G-LAVKA-GOOD', 'GOOD-SEMYONYCH-RARE': 'CL-STORE-G-LAVKA-RARE',
  'GOOD-SEMYONYCH-EPIC': 'CL-STORE-G-LAVKA-EPIC', 'GOOD-SEMYONYCH-S8': 'CL-STORE-G-LAVKA-S8',
  'GOOD-RENAME': 'CL-STORE-G-RENAME', 'GOOD-FRAME': 'CL-STORE-G-FRAME', 'GOOD-COOP-EMBLEM': 'CL-STORE-G-EMBLEM', 'GOOD-PASS': 'CL-STORE-G-PASS',
};
const GOOD_ICONS: Record<string, IconName> = {
  'GOOD-THERMOS': 'energy', 'GOOD-BOOST-W01': 'map', 'GOOD-NUTS-BUCKET': 'nuts', 'GOOD-RENAME': 'hand', 'GOOD-FRAME': 'sparks',
};

export function goodName(g: TalonGood): string {
  if (g.defId) return itemName(g.defId);
  const k = GOOD_KEYS[g.productId];
  if (k) return tr(k);
  return g.productId.startsWith('C-') ? tr('CL-STORE-G-LOOK') : tr('CL-STORE-G-ANY');
}

export function GoodIcon(p: { g: TalonGood; size: number }) {
  if (p.g.defId) return <Icon id={p.g.defId} fallback="🧰" size={p.size} />;
  if (p.g.boxDefId) return <Icon id={boxArt(p.g.boxDefId)} fallback="📦" size={p.size} />;
  if (p.g.productId.startsWith('GOOD-SEMYONYCH')) return <I n="semenych" size={p.size} />;
  // Своей картинки у косметики пока нет (арт): значок магазина из манифеста, а не эмодзи.
  if (p.g.productId.startsWith('C-') || p.g.section === 'looks') return assetEntry(`ICO-${p.g.productId}`) ? <Icon id={`ICO-${p.g.productId}`} fallback="" size={p.size} /> : <I n="shop" size={p.size} />;
  if (p.g.productId === 'GOOD-BOOST-W01') return <Icon id="ICO-BOSS-W01" fallback="" size={p.size} />;
  return <I n={GOOD_ICONS[p.g.productId] ?? 'shop'} size={p.size} />;
}

/** Строка предела под ценой (§1.3: «у каждой плитки — лимит строкой под ценой»). */
export function limitLine(g: TalonGood): string | null {
  if (g.firstFree) return tr('CL-STORE-FIRST-FREE');
  const l = g.limit;
  if (!l) return null;
  if (l.per === 'day') return tr('CL-STORE-LIMIT-DAY', { k: Math.min(l.used, l.total), n: l.total });
  if (l.per === 'week') return l.total === 1 ? tr('CL-STORE-LIMIT-WEEK-ONE') : tr('CL-STORE-LIMIT-WEEK', { k: Math.min(l.used, l.total), n: l.total });
  if (l.per === 'season') return tr('CL-STORE-LIMIT-SEASON');
  return tr('CL-STORE-LIMIT-ONCE');
}

/** Текст кнопки и её доступность для состояния покупки (§2.1). */
export function buyLabel(st: BuyState): { text: string; disabled: boolean } {
  switch (st.kind) {
    case 'ok': return { text: tr('CL-STORE-BUY-FOR', tn(st.price)), disabled: false };
    case 'lack': return { text: tr('CL-STORE-LACK', tn(st.deficit)), disabled: false };
    case 'debt': return { text: tr('CL-STORE-DEBT-PRICE'), disabled: true };
    case 'limit': return { text: st.per === 'week' ? tr('CL-STORE-AGAIN-MONDAY') : st.per === 'season' ? tr('CL-STORE-AGAIN-SEASON') : tr('CL-STORE-AGAIN-TOMORROW'), disabled: true };
    case 'bought': return { text: tr('SHOP-SHOWCASE-BOUGHT'), disabled: true };
    case 'blocked': return { text: st.reason === 'paid_boxes_off' ? tr('CL-STORE-BOXES-OFF') : tr('CL-STORE-SOON'), disabled: true };
    case 'noprice': return { text: tr('CL-STORE-SOON'), disabled: true };
  }
}

/** Плитка товара за талоны: значок, название, предел, цена с ориентиром, кнопка-цена. */
export function GoodTile(p: { g: TalonGood; rate: number; rub: number | null; off: boolean; onOdds?: () => void }) {
  const app = useApp();
  const w = wallet.value;
  const st = buyState(p.g, w?.talons ?? 0, inDebt(w));
  const lab = buyLabel(st);
  const lim = limitLine(p.g);
  const price = p.g.priceTalons;
  return (
    <div class="card store-tile good-tile" data-testid={`good-${p.g.productId}`}>
      <button class="good-open" onClick={() => { app.haptic('selection'); app.nav.openSheet('MOD-PURCHASE', { good: p.g }); }} aria-label={goodName(p.g)}>
        <GoodIcon g={p.g} size={36} />
        <b class="good-name">{goodName(p.g)}</b>
        {lim ? <span class="small muted">{lim}</span> : null}
        {price !== null && st.kind !== 'blocked' && st.kind !== 'debt' ? <TalonPrice n={price} hint={talonsHint(price, p.rate, p.rub)} /> : null}
        <span class={`btn small ${p.off || lab.disabled ? 'disabled' : 'accent'}`} style={{ width: '100%' }}>
          {p.off ? tr('UI-OFFLINE') : st.kind === 'ok' || st.kind === 'lack' ? <><I n="talons" size={16} />{num(st.price)}</> : lab.text}
        </span>
      </button>
      {p.onOdds ? <button class="odds-link" data-testid={`odds-${p.g.productId}`} onClick={p.onOdds}>{tr('CL-STORE-ODDS')}</button> : null}
    </div>
  );
}

function Stub(p: { text: string; testId: string; icon?: IconName }) {
  return <div class="empty store-stub" data-testid={p.testId}><I n={p.icon ?? 'shop'} size={44} class="ill" />{p.text}</div>;
}

function Grid(p: { goods: TalonGood[]; rate: number; rub: number | null; off: boolean; odds?: (g: TalonGood) => (() => void) | undefined }) {
  return <div class="store-grid">{p.goods.map((g) => <GoodTile key={g.productId} g={g} rate={p.rate} rub={p.rub} off={p.off} {...(p.odds?.(g) ? { onOdds: p.odds(g)! } : {})} />)}</div>;
}

interface TabProps { shop: ShopData; ex: StoreExtras; off: boolean }

/** «Наборы»: личные предложения плиткой; наборы событий с тиражом — этап Б (E12-T13). */
export function SetsTab({ shop, ex, off }: TabProps) {
  const app = useApp();
  const rate = baseRate(starPacks(shop));
  if (!shop.personalOffers.length) return <Stub testId="store-sets-empty" text={tr('CL-STORE-SETS-EMPTY')} icon="party" />;
  return (
    <div class="store-grid">
      {shop.personalOffers.map((o) => (
        <button key={o.offerId} class="card store-tile" data-testid={`offer-${o.offerId}`} onClick={() => app.nav.openSheet('MOD-PURCHASE', { product: { productId: o.productId, priceXtr: o.priceXtr, grant: o.grant, badge: null }, rate })}>
          <span class="badge store-badge">{tr('CL-STORE-FOR-YOU')}</span>
          <I n="party" size={36} />
          <GrantLine g={o.grant} />
          {o.expiresAt ? <span class="small muted">{tr('CL-STORE-UNTIL', { t: new Date(o.expiresAt).toLocaleDateString('ru-RU') })}</span> : null}
          <span class={`btn small ${off ? 'disabled' : 'accent'}`} style={{ width: '100%' }}>{off ? tr('UI-OFFLINE') : <Stars n={o.priceXtr} />}</span>
          <HintLine hint={{ stars: null, rub: ex.rubPerStar ? Math.round(o.priceXtr * ex.rubPerStar) : null }} />
        </button>
      ))}
    </div>
  );
}

export function GrantLine(p: { g: Grant }) {
  const cur = Object.entries(p.g.currencies ?? {}).map(([k, v]) => amount(k, v));
  const items = (p.g.items ?? []).map((i) => `${itemName(i.defId)}${i.qty > 1 ? ` ×${i.qty}` : ''}`);
  return <span class="small">{[...cur, ...items].join(' · ')}</span>;
}

/** «Лавка»: блок «Витрины председателя» сверху (E12-T21) и «Лавка Семёныча» ниже (§1.3). */
export function LavkaTab({ shop, ex, off, showcase }: TabProps & { showcase: { data: ResponseOf<Api['getShowcase']> | null; error: boolean } }) {
  const app = useApp();
  const rate = baseRate(starPacks(shop));
  const lavka = ex.goods.filter((g) => g.section === 'shop' && g.productId !== 'GOOD-SHOWCASE');
  const sc = showcase.data;
  const refresh = lavka.find((g) => g.refreshAt)?.refreshAt;
  return (
    <div class="col">
      <button class="card showcase-block" data-testid="showcase-block" disabled={off && !sc} onClick={() => app.nav.openSheet('MOD-SHOWCASE')}>
        <span class="row" style={{ gap: 8 }}><I n="sparks" size={28} /><b>{tr('SHOP-SHOWCASE-TITLE')}</b><span class="spacer" />{sc ? <span class="badge soft" data-testid="showcase-week">{tr('SHOP-SHOWCASE-WEEK', { k: sc.weekPurchases, limit: sc.weeklyLimit })}</span> : null}</span>
        <span class="small muted">{tr('SHOP-SHOWCASE-HONEST')}</span>
        {sc ? (
          <span class="showcase-mini">
            {sc.items.map((it) => (
              <span key={it.pos} class={`item-tile rarity-legendary ${it.purchased ? 'bought' : ''}`}>
                <span class="glyph"><Icon id={it.defId} fallback="🧰" size={30} /></span>
                <span class="nm">{it.purchased ? tr('SHOP-SHOWCASE-BOUGHT') : itemName(it.defId)}</span>
              </span>
            ))}
          </span>
        ) : showcase.error ? <span class="small muted">{tr('CL-STORE-SHOWCASE-ERR')}</span> : <span class="skeleton" style={{ height: 56 }} />}
      </button>
      <div class="row lavka-head"><I n="semenych" size={24} /><b>{tr('CL-STORE-LAVKA')}</b><span class="spacer" />{refresh ? <span class="small muted">{tr('CL-STORE-LAVKA-REFRESH', { d: daysLeft(refresh) })}</span> : null}</div>
      {lavka.length ? <Grid goods={lavka} rate={rate} rub={ex.rubPerStar} off={off} /> : <Stub testId="store-lavka-empty" text={tr('CL-STORE-LAVKA-EMPTY')} icon="semenych" />}
    </div>
  );
}

function daysLeft(iso: string): number {
  return Math.max(1, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000));
}

/** «Ящики»: «Ящик мастера» ×1/×10 с «Шансы», счётчиком гаранта и худшим случаем на плитке (§1.3). */
export function BoxesTab({ shop, ex, off }: TabProps) {
  const app = useApp();
  const rate = baseRate(starPacks(shop));
  const boxes = ex.goods.filter((g) => g.section === 'boxes');
  if (!boxes.length) return <Stub testId="store-boxes-empty" text={tr('CL-STORE-BOXES-EMPTY')} icon="ko" />;
  const one = boxes.find((g) => (g.qty ?? 1) === 1 && g.pityLeft !== undefined);
  return (
    <div class="col">
      {boxes.some((g) => g.blocked === 'paid_boxes_off') ? <div class="banner" data-testid="boxes-off">{tr('CL-STORE-BOXES-OFF-WHY')}</div> : null}
      {one && one.pityLeft !== undefined && one.priceTalons ? (
        <div class="banner" data-testid="box-pity">
          {tr('CL-STORE-PITY', { g: one.pityLeft })}{one.pityLegendary !== undefined ? ` · ${tr('CL-STORE-PITY-LEG', { g: one.pityLegendary })}` : ''} · {tr('CL-STORE-WORST', tn(one.pityLeft * one.priceTalons))}
        </div>
      ) : null}
      <Grid goods={boxes} rate={rate} rub={ex.rubPerStar} off={off} odds={(g) => () => app.nav.openSheet('MOD-ODDS', { source: g.boxDefId ?? 'BOX-MASTER', ...(g.pityLeft !== undefined ? { pityLeft: g.pityLeft } : {}) })} />
    </div>
  );
}

/** «Энергия»: только термос — остаток «3 из 5 сегодня» и цена следующего (§1.3, E12-T14). */
export function EnergyTab({ shop, ex, off }: TabProps) {
  const app = useApp();
  const rate = baseRate(starPacks(shop));
  const th = useThermos();
  const g = ex.goods.find((x) => x.productId === 'GOOD-THERMOS');
  if (!th && !g) return <Stub testId="store-energy-empty" text={tr('CL-STORE-SOON-TAB')} icon="energy" />;
  const left = th?.leftToday ?? 0;
  const price = th?.nextPriceTalons ?? g?.priceTalons ?? null;
  const done = !!th && left <= 0;
  return (
    <div class="col">
      <button class="card thermos-card" data-testid="thermos-tile" disabled={done || off} onClick={() => { app.haptic('selection'); app.nav.openSheet('MOD-THERMOS'); }}>
        <I n="energy" size={44} />
        <span class="col" style={{ gap: 2, flex: 1, alignItems: 'flex-start', textAlign: 'left' }}>
          <b>{tr('CL-STORE-G-THERMOS')}</b>
          <span class="small">{th ? tr('CL-STORE-THERMOS-LEFT', { k: left, n: th.perDay }) : null}</span>
          {done ? <span class="small muted" data-testid="thermos-tomorrow">{tr('CL-STORE-AGAIN-TOMORROW')}</span> : price !== null ? <TalonPrice n={price} hint={talonsHint(price, rate, ex.rubPerStar)} /> : null}
        </span>
        <span class={`btn small ${done || off ? 'disabled' : 'accent'}`}>{off ? tr('UI-OFFLINE') : done ? tr('CL-STORE-DONE') : price !== null ? <><I n="talons" size={16} />{num(price)}</> : tr('CL-STORE-THERMOS-OPEN')}</span>
      </button>
      <p class="small muted" style={{ margin: 0 }}>{tr('CL-STORE-NO-ADS')}</p>
    </div>
  );
}

/** «Ускорения»: «+100% урона на сутки» только в неделю W01; вне рейда и в неделю сорванного сбора — заглушки (§1.3). */
export function BoostsTab({ shop, ex, off }: TabProps) {
  const rate = baseRate(starPacks(shop));
  const b = ex.goods.filter((g) => g.section === 'boosts');
  const live = b.filter((g) => g.blocked !== 'raid_off' && g.blocked !== 'raid_failed' && g.blocked !== 'not_yet');
  if (b.some((g) => g.blocked === 'raid_failed')) return <Stub testId="store-boosts-failed" text={tr('CL-STORE-BOOST-FAILED')} icon="map" />;
  if (!live.length) return <Stub testId="store-boosts-off" text={tr('CL-STORE-BOOST-OFF')} icon="map" />;
  return <Grid goods={live} rate={rate} rub={ex.rubPerStar} off={off} />;
}

/** «Вид»: косметика за талоны; пока системы косметики нет (Р-65), плитки видны с «Скоро». */
export function LooksTab({ shop, ex, off }: TabProps) {
  const rate = baseRate(starPacks(shop));
  const looks = ex.goods.filter((g) => g.section === 'looks');
  if (!looks.length) return <Stub testId="store-looks-empty" text={tr('CL-STORE-SOON-TAB')} icon="sparks" />;
  return <div class="col"><p class="small muted" style={{ margin: 0 }}>{tr('SHOP-LOOK-NOTE')}</p><Grid goods={looks} rate={rate} rub={ex.rubPerStar} off={off} /></div>;
}

/** «Всякое»: «Ведро гаек» (≤ 3 в сутки), смена имени (§1.3). */
export function MiscTab({ shop, ex, off }: TabProps) {
  const rate = baseRate(starPacks(shop));
  const misc = ex.goods.filter((g) => g.section === 'misc' && !g.productId.startsWith('GOOD-SPEEDUP'));
  if (!misc.length) return <Stub testId="store-misc-empty" text={tr('CL-STORE-SOON-TAB')} />;
  return <Grid goods={misc} rate={rate} rub={ex.rubPerStar} off={off} />;
}

// ---------------- MOD-PURCHASE: товар за талоны ----------------

const BUY_ERR: Record<string, string> = {
  NOT_ENOUGH_TALONS: 'CL-STORE-ERR-TALONS', DAILY_LIMIT: 'CL-STORE-ERR-DAY', WEEKLY_LIMIT: 'CL-STORE-ERR-WEEK',
  TALONS_DEBT: 'SHOP-DEBT', GONE: 'CL-SCREENS-STORE-29', ALREADY_PURCHASED: 'CL-STORE-ERR-BOUGHT',
};

/** Ответ сервера после покупки за талоны → окно результата (ящик — сразу к открытию, §1.3). */
export function afterTalonBuy(app: ReturnType<typeof useApp>, g: TalonGood, r: ResponseOf<Api['buyForTalons']>): void {
  setWallet(r.wallet);
  bumpShop();
  app.haptic('success');
  const got = r.grants.items?.find((i) => i.defId.startsWith('BOX-'));
  const box = g.boxDefId ?? got?.defId;
  if (box) { app.nav.openSheet('MOD-BOX-OPEN', { boxDefId: box, qty: got?.qty ?? g.qty ?? 1 }, { replace: true }); return; }
  app.nav.openSheet('MOD-REWARD', { grant: r.grants }, { replace: true });
}

export function TalonPurchaseBody(p: { g: TalonGood; entryParams: Record<string, unknown>; shop: ShopData | null }) {
  const app = useApp();
  const g = p.g;
  const w = wallet.value;
  const ex = useExtras(p.shop);
  const packs = starPacks(p.shop);
  const rate = baseRate(packs);
  const st = buyState(g, w?.talons ?? 0, inDebt(w));
  const lab = buyLabel(st);
  const lim = limitLine(g);
  const cover = st.kind === 'lack' ? coverPack(st.deficit, packs.filter((x) => !x.blocked)) : null;
  // Один ключ на окно: повтор после обрыва не купит второй раз; после ответа сервера — новый.
  const key = useRef(app.idem());
  const buy = async () => {
    try {
      const r = await app.call('buyForTalons', { body: { productId: g.productId, qty: 1 }, idempotencyKey: key.current });
      key.current = app.idem();
      afterTalonBuy(app, g, r);
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.current = app.idem();
      if (code === 'NOT_ENOUGH_TALONS') void app.call('getWallet').then(setWallet).catch(() => {});
      if (BUY_ERR[code]) { app.haptic('error'); showToast(tr(BUY_ERR[code]!), 'error'); bumpShop(); return; }
      if (code === 'INTERNAL' || code === 'NETWORK' || code === 'TIMEOUT') { app.haptic('error'); showToast(tr('CL-STORE-ERR-SAFE'), 'error'); return; }
      app.handleError(e, { resource: tr('CL-STORE-TALONS-SAFE') });
    }
  };
  const toPack = () => {
    if (!cover) return;
    app.nav.openSheet('MOD-PURCHASE', { product: cover.pack, rate, returnTo: { id: 'MOD-PURCHASE', params: p.entryParams } });
  };
  return (
    <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="purchase-talons">
      <GoodIcon g={g} size={64} />
      <b style={{ fontSize: 20 }}>{goodName(g)}</b>
      {g.defId ? <span class="small muted">{itemDesc(g.defId)}</span> : null}
      {g.defId && g.stats ? <div style={{ width: '100%', textAlign: 'left' }}><StatsCompare stats={g.stats} slot={slotOf(g.defId)} testId="lavka-stats" /></div> : null}
      {lim ? <span class="badge soft">{lim}</span> : null}
      {g.blocked === 'paid_boxes_off' ? <div class="banner" data-testid="purchase-boxes-off">{tr('CL-STORE-BOXES-OFF-WHY')}</div> : null}
      {g.priceTalons !== null ? <TalonPrice n={g.priceTalons} hint={talonsHint(g.priceTalons, rate, ex.rubPerStar)} /> : null}
      <span class="small muted" data-testid="have-talons">{tr('CL-STORE-HAVE', tn(w?.talons ?? 0))}</span>
      {st.kind === 'debt' ? <div class="banner warn" data-testid="debt-banner">{tr('CL-STORE-DEBT-BANNER', tn(w?.talonsDebt ?? Math.abs(Math.min(0, w?.talons ?? 0))))}</div> : null}
      {cover ? (
        <button class="banner lack-line" data-testid="lack-stars" onClick={toPack}>
          {tr('CL-STORE-LACK-STARS', { n: num(cover.stars) })}<StarsIcon size={14} /><span class="spacer" /><b>{tr('CL-STORE-TO-PACK', { name: PACK_NAMES[cover.pack.productId] ?? '' })}</b>
        </button>
      ) : null}
      {g.boxDefId ? <button class="odds-link" data-testid="purchase-odds" onClick={() => app.nav.openSheet('MOD-ODDS', { source: g.boxDefId })}>{tr('CL-STORE-ODDS')}</button> : null}
      <div class="purchase-cta">
        {st.kind === 'lack'
          ? <HoldButton text={lab.text} hold={false} testId="buy-talons" disabled={!online.value || !cover} onDone={toPack} />
          : <HoldButton text={!online.value ? tr('UI-OFFLINE') : lab.text} hold={st.kind === 'ok' && st.hold} testId="buy-talons" disabled={!online.value || lab.disabled} onDone={buy} />}
      </div>
      <p class="small muted" style={{ margin: 0 }}>{tr('CL-STORE-TALON-NOTE')}</p>
    </div>
  );
}
